import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";

// Custom car pin icon
const carIcon = L.divIcon({
  className: "velocity-pin",
  html: `<div style="width:34px;height:34px;background:linear-gradient(135deg,#3B82F6,#06B6D4);border-radius:12px;display:flex;align-items:center;justify-content:center;box-shadow:0 8px 24px -6px rgba(59,130,246,0.6),0 0 0 3px rgba(11,13,18,0.9);transform:translate(-50%,-100%);">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0B0D12" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>
  </div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 34],
});

const gigIcon = L.divIcon({
  className: "velocity-pin-gig",
  html: `<div style="width:34px;height:34px;background:#10B981;border-radius:12px;display:flex;align-items:center;justify-content:center;box-shadow:0 8px 24px -6px rgba(16,185,129,0.7),0 0 0 3px rgba(11,13,18,0.9);transform:translate(-50%,-100%);">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0B0D12" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
  </div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 34],
});

function Recenter({ cars }) {
  const map = useMap();
  useEffect(() => {
    const pts = cars.filter((c) => c.lat && c.lng).map((c) => [c.lat, c.lng]);
    if (pts.length === 0) return;
    if (pts.length === 1) { map.setView(pts[0], 11); return; }
    map.fitBounds(pts, { padding: [40, 40] });
  }, [cars, map]);
  return null;
}

export default function CarsMap({ cars }) {
  const containerRef = useRef(null);
  const withCoords = cars.filter((c) => c.lat && c.lng);
  const center = withCoords[0] ? [withCoords[0].lat, withCoords[0].lng] : [37.0902, -95.7129];

  return (
    <div ref={containerRef} className="relative h-[560px] rounded-3xl overflow-hidden border border-slate-800" data-testid="cars-map">
      <MapContainer center={center} zoom={4} scrollWheelZoom className="h-full w-full z-0">
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
        {withCoords.map((c) => (
          <Marker key={c.car_id} position={[c.lat, c.lng]} icon={c.gig_ready ? gigIcon : carIcon}>
            <Popup className="velocity-popup">
              <Link to={`/cars/${c.car_id}`} className="block w-56" data-testid={`map-popup-${c.car_id}`}>
                {c.photos?.[0] && <img src={c.photos[0]} alt="" className="w-full h-28 object-cover rounded-md mb-2" />}
                <div className="font-display font-bold text-slate-900">{c.year} {c.make} {c.model}</div>
                <div className="text-xs text-slate-600 mb-1">{c.city}</div>
                <div className="font-mono text-sm text-blue-600 font-bold">${c.daily_price}/day</div>
              </Link>
            </Popup>
          </Marker>
        ))}
        <Recenter cars={withCoords} />
      </MapContainer>
      <div className="absolute bottom-4 left-4 glass rounded-full px-4 py-2 text-xs font-mono uppercase tracking-widest flex items-center gap-3 z-[400]">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-blue-500 to-cyan-400" />Standard</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Gig-ready</span>
      </div>
    </div>
  );
}
